/**
 * AxisIcon — F-031 (2026-05-24). F-038 (2026-05-26): cross axis retired.
 *
 * Shared icon for the Energy / Work / Love axes. The same vocabulary is
 * rendered as SF Symbols on macOS (bolt.fill / briefcase.fill /
 * heart.fill) and as inline SVG on the web so a client looking at their
 * Hero Tracker sees the SAME glyph their coach sees on the Carpe Diem
 * matrix.
 *
 * Inline SVG (no extra dep). Pulls the brand flame ramp for tint:
 *   - energy → flame
 *   - work   → flame-deep
 *   - love   → flame-ember
 *
 * Use <AxisIcon /> for a bare glyph and <AxisChip /> for icon + label
 * pill (matches the macOS AxisChip shape).
 */

import { useTranslation } from 'react-i18next';

export type Axis = 'energy' | 'work' | 'love';

interface AxisIconProps {
  axis: Axis;
  /** Pixel size — defaults to 14px, matching macOS body-row chips. */
  size?: number;
  /** When true (default), tint with the brand colour for the axis. */
  tinted?: boolean;
  /** Optional override for the colour class — overrides `tinted`. */
  className?: string;
  /** Aria label override. Defaults to the localised axis name. */
  ariaLabel?: string;
}

const TINT_CLASS: Record<Axis, string> = {
  energy: 'text-flame',
  work: 'text-flame-deep',
  love: 'text-flame-ember',
};

export function AxisIcon({
  axis,
  size = 14,
  tinted = true,
  className,
  ariaLabel,
}: AxisIconProps): JSX.Element {
  const { t } = useTranslation();
  const cls = className ?? (tinted ? TINT_CLASS[axis] : 'text-gray-2');
  const label = ariaLabel ?? t(`common.axis.${axis}`);
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'currentColor',
    'aria-label': label,
    role: 'img' as const,
    className: cls,
  };
  switch (axis) {
    case 'energy':
      // Lightning bolt — matches SF Symbol `bolt.fill`.
      return (
        <svg {...common}>
          <path d="M13.5 2 4 14h6l-1.5 8 9.5-12h-6L13.5 2z" />
        </svg>
      );
    case 'work':
      // Briefcase — matches SF Symbol `briefcase.fill`.
      return (
        <svg {...common}>
          <path d="M9 3.5A1.5 1.5 0 0 1 10.5 2h3A1.5 1.5 0 0 1 15 3.5V5h4a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4V3.5zm1.5.5v1h3V4h-3z" />
        </svg>
      );
    case 'love':
      // Heart — matches SF Symbol `heart.fill`.
      return (
        <svg {...common}>
          <path d="M12 21s-7.5-4.4-9.7-9.1A5.4 5.4 0 0 1 7.7 4.6 5.7 5.7 0 0 1 12 6.7a5.7 5.7 0 0 1 4.3-2.1 5.4 5.4 0 0 1 5.4 7.3C19.5 16.6 12 21 12 21z" />
        </svg>
      );
  }
}

interface AxisChipProps {
  axis: Axis;
  /** If true (default), tint the chip body with a soft axis-tinted bg. */
  selected?: boolean;
  onClick?: () => void;
  /** When provided, renders as a button; otherwise a plain span. */
  as?: 'button' | 'span';
  /** Override the label — defaults to the localised axis name. */
  label?: string;
  testId?: string;
}

const SELECTED_BG: Record<Axis, string> = {
  energy: 'bg-flame/15 border-flame text-flame-deep',
  work: 'bg-flame-deep/15 border-flame-deep text-flame-deep',
  love: 'bg-flame-ember/15 border-flame-ember text-flame-ember',
};

export function AxisChip({
  axis,
  selected = false,
  onClick,
  as,
  label,
  testId,
}: AxisChipProps): JSX.Element {
  const { t } = useTranslation();
  const text = label ?? t(`common.axis.${axis}`);
  const cls = selected
    ? `${SELECTED_BG[axis]} border`
    : 'bg-bone border border-bone-3 text-gray-2 hover:text-ink';
  const className = `inline-flex items-center gap-1 px-s2 py-1 rounded-full text-xs font-mono uppercase tracking-wide ${cls}`;
  const inner = (
    <>
      <AxisIcon axis={axis} size={11} tinted={selected} />
      <span>{text}</span>
    </>
  );
  const Element = as ?? (onClick ? 'button' : 'span');
  if (Element === 'button') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={className}
        aria-pressed={selected}
        data-testid={testId}
      >
        {inner}
      </button>
    );
  }
  return (
    <span className={className} data-testid={testId}>
      {inner}
    </span>
  );
}
